import { useState, useEffect, useMemo } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, CheckIcon, PlusCircleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import type { Flow, Colaborador } from '../../types';
import { apiUrl } from '../../utils/apiBase';
import { ehAdminMaster } from '../../utils/adminMaster';

interface ConfigColaborador {
  colaboradorId: string;
  podeCriarFluxos: boolean;
  visibilidadeFluxos: 'todos' | 'selecionados';
  fluxosPermitidos: string[]; // IDs dos fluxos visíveis quando visibilidade = 'selecionados'
}

interface ConfigFluxosGlobalProps {
  flows: Flow[];
  colaboradores: Colaborador[];
  empresaId: string;
  currentUser?: any;
  onClose: () => void;
}

const getInitials = (nome: string) => nome?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || '?';

const AVATAR_COLORS = [
  'bg-indigo-500', 'bg-purple-500', 'bg-pink-500', 'bg-blue-500',
  'bg-emerald-500', 'bg-orange-500', 'bg-teal-500', 'bg-rose-500',
];

const ConfigFluxosGlobal = ({ flows, colaboradores, empresaId, currentUser, onClose }: ConfigFluxosGlobalProps) => {
  const userHeader = {
    email: currentUser?.email || '',
    role: currentUser?.role || 'admin',
    empresaId: empresaId,
  };
  const [configs, setConfigs] = useState<ConfigColaborador[]>([]);
  const [selecionado, setSelecionado] = useState<string | null>(null);
  const [pesquisa, setPesquisa] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [carregando, setCarregando] = useState(true);

  // Carregar configurações do backend
  useEffect(() => {
    setCarregando(true);
    fetch(apiUrl(`/flows/config/permissoes?empresaId=${empresaId}`), {
        headers: { 'X-User-Data': JSON.stringify(userHeader) }
      })
      .then(r => r.json())
      .then(data => {
        if (data.configs && Array.isArray(data.configs)) {
          setConfigs(data.configs);
        }
      })
      .catch(() => {})
      .finally(() => setCarregando(false));
  }, [empresaId]);

  const colaboradoresFiltrados = useMemo(() => {
    const q = pesquisa.toLowerCase();
    return colaboradores.filter(c =>
      !q || c.nome?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q)
    );
  }, [colaboradores, pesquisa]);

  const getConfig = (colaboradorId: string): ConfigColaborador => {
    return configs.find(c => c.colaboradorId === colaboradorId) || {
      colaboradorId,
      podeCriarFluxos: true,
      visibilidadeFluxos: 'todos',
      fluxosPermitidos: [],
    };
  };

  const updateConfig = (colaboradorId: string, partial: Partial<ConfigColaborador>) => {
    setConfigs(prev => {
      const idx = prev.findIndex(c => c.colaboradorId === colaboradorId);
      const atual = getConfig(colaboradorId);
      const novo = { ...atual, ...partial };
      if (idx >= 0) {
        const copia = [...prev];
        copia[idx] = novo;
        return copia;
      }
      return [...prev, novo];
    });
  };

  const toggleFluxoPermitido = (colaboradorId: string, flowId: string) => {
    const conf = getConfig(colaboradorId);
    const lista = conf.fluxosPermitidos || [];
    const nova = lista.includes(flowId)
      ? lista.filter(id => id !== flowId)
      : [...lista, flowId];
    updateConfig(colaboradorId, { fluxosPermitidos: nova });
  };

  const handleSalvar = async () => {
    setSalvando(true);
    try {
      const res = await fetch(apiUrl(`/flows/config/permissoes`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Data': JSON.stringify(userHeader),
        },
        body: JSON.stringify({ empresaId, configs }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onClose();
    } catch (e) {
      alert('Erro ao salvar configurações.');
    } finally {
      setSalvando(false);
    }
  };

  const configAtual = selecionado ? getConfig(selecionado) : null;
  const colaboradorAtual = selecionado ? colaboradores.find(c => String(c.id || (c as any)._id) === selecionado) : null;

  // 👑 Admin master: único isento da config global — o toggle vale para todos os demais
  const ehMaster = ehAdminMaster(colaboradorAtual?.email);

  // Colaboradores com config não-padrão (destaque visual)
  const comConfigCustom = new Set(configs.filter(c => !c.podeCriarFluxos || c.visibilidadeFluxos === 'selecionados').map(c => c.colaboradorId));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-900">Configuração Global de Fluxos</h2>
            <p className="text-xs text-gray-500 mt-0.5">Defina o que cada colaborador pode fazer e ver nos fluxos</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Lista de colaboradores */}
          <div className="w-64 border-r border-gray-100 flex flex-col shrink-0">
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  value={pesquisa}
                  onChange={e => setPesquisa(e.target.value)}
                  placeholder="Buscar colaborador..."
                  className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {carregando ? (
                <div className="p-4 text-center text-xs text-gray-400">Carregando...</div>
              ) : (
                colaboradoresFiltrados.map((colab, idx) => {
                  const id = String(colab.id || (colab as any)._id);
                  const isSelected = selecionado === id;
                  const temCustom = comConfigCustom.has(id);
                  const conf = getConfig(id);
                  return (
                    <button
                      key={id}
                      onClick={() => setSelecionado(id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-gray-50 ${isSelected ? 'bg-indigo-50 border-r-2 border-indigo-500' : ''}`}
                    >
                      <div className={`w-7 h-7 rounded-full ${AVATAR_COLORS[idx % AVATAR_COLORS.length]} flex items-center justify-center text-white text-[10px] font-bold shrink-0 overflow-hidden`}>
                        {colab.foto ? <img src={colab.foto} alt="" className="w-full h-full object-cover" /> : getInitials(colab.nome)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-800 truncate">{colab.nome}</div>
                        <div className="flex items-center gap-1 mt-0.5">
                          {!conf.podeCriarFluxos && (
                            <span className="text-[9px] bg-red-100 text-red-600 px-1 rounded">sem criação</span>
                          )}
                          {conf.visibilidadeFluxos === 'selecionados' && (
                            <span className="text-[9px] bg-amber-100 text-amber-600 px-1 rounded">{conf.fluxosPermitidos.length} fluxo(s)</span>
                          )}
                          {!temCustom && (
                            <span className="text-[9px] text-gray-400">padrão</span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Painel de configuração */}
          <div className="flex-1 overflow-y-auto p-5">
            {!selecionado ? (
              <div className="h-full flex flex-col items-center justify-center text-center text-gray-400">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                </div>
                <p className="text-sm font-medium text-gray-500">Selecione um colaborador</p>
                <p className="text-xs mt-1">para configurar suas permissões de fluxo</p>
              </div>
            ) : (
              <div>
                {/* Cabeçalho colaborador */}
                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
                  <div className={`w-9 h-9 rounded-full ${AVATAR_COLORS[colaboradores.findIndex(c => String(c.id || (c as any)._id) === selecionado) % AVATAR_COLORS.length]} flex items-center justify-center text-white text-sm font-bold overflow-hidden`}>
                    {colaboradorAtual?.foto ? <img src={colaboradorAtual.foto} alt="" className="w-full h-full object-cover" /> : getInitials(colaboradorAtual?.nome || '')}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{colaboradorAtual?.nome}</div>
                    <div className="text-xs text-gray-500">{colaboradorAtual?.email}</div>
                  </div>
                </div>

                {/* Permissão: Criar Fluxos */}
                <div className="mb-5">
                  <div className="flex items-center justify-between p-3.5 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <PlusCircleIcon className="w-4 h-4 text-indigo-600" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-800">Pode criar fluxos</div>
                        <div className="text-xs text-gray-500">
                          {ehMaster
                            ? 'Admin master — sempre pode criar fluxos'
                            : 'Permite criar novos fluxos de trabalho'}
                        </div>
                      </div>
                    </div>
                    <button
                      disabled={ehMaster}
                      onClick={() => updateConfig(selecionado, { podeCriarFluxos: !configAtual!.podeCriarFluxos })}
                      className={`relative w-10 h-5.5 rounded-full transition-colors duration-200 ${ehMaster ? 'bg-indigo-500 opacity-50 cursor-not-allowed' : (configAtual!.podeCriarFluxos ? 'bg-indigo-500' : 'bg-gray-300')}`}
                      style={{ minWidth: '2.5rem', height: '1.375rem' }}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${(ehMaster || configAtual!.podeCriarFluxos) ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>

                {/* Visibilidade de fluxos */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <EyeIcon className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-semibold text-gray-800">Visibilidade de Fluxos</span>
                  </div>

                  <div className="space-y-2 mb-4">
                    {(['todos', 'selecionados'] as const).map(opcao => (
                      <label key={opcao} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${configAtual!.visibilidadeFluxos === opcao ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <input
                          type="radio"
                          name={`visibilidade-${selecionado}`}
                          checked={configAtual!.visibilidadeFluxos === opcao}
                          onChange={() => updateConfig(selecionado, { visibilidadeFluxos: opcao })}
                          className="mt-0.5 accent-indigo-600"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-800">
                            {opcao === 'todos' ? 'Todos os fluxos' : 'Apenas fluxos selecionados'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {opcao === 'todos'
                              ? 'O colaborador vê todos os fluxos da empresa'
                              : 'Escolha quais fluxos este colaborador pode acessar'}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>

                  {/* Lista de fluxos para selecionar */}
                  {configAtual!.visibilidadeFluxos === 'selecionados' && (
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                        <span className="text-xs font-medium text-gray-600">Fluxos permitidos — {configAtual!.fluxosPermitidos.length} selecionado(s)</span>
                      </div>
                      <div className="max-h-52 overflow-y-auto divide-y divide-gray-100">
                        {flows.map(flow => {
                          const permitido = configAtual!.fluxosPermitidos.includes(flow.id);
                          return (
                            <button
                              key={flow.id}
                              onClick={() => toggleFluxoPermitido(selecionado, flow.id)}
                              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors ${permitido ? 'bg-indigo-50/50' : ''}`}
                            >
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${permitido ? 'bg-indigo-500 border-indigo-500' : 'border-gray-300'}`}>
                                {permitido && <CheckIcon className="w-3 h-3 text-white" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium text-gray-800 truncate">{flow.name}</div>
                                <div className="text-[10px] text-gray-400">{flow.etapas?.length || 0} etapas · {flow.cards?.length || 0} cartões</div>
                              </div>
                              {permitido ? (
                                <EyeIcon className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                              ) : (
                                <EyeSlashIcon className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-between items-center shrink-0">
          <p className="text-xs text-gray-400">
            {configs.filter(c => !c.podeCriarFluxos || c.visibilidadeFluxos === 'selecionados').length} colaborador(es) com configuração personalizada
          </p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
            <button
              onClick={handleSalvar}
              disabled={salvando}
              className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60 transition-colors font-medium"
            >
              {salvando ? 'Salvando...' : 'Salvar Configurações'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigFluxosGlobal;
